import React from 'react';
import { Table, Icon, Button, Card } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import ColorDots from './colorDots';
import { useZonesTree } from '../../charts/chart/zone/hooks/useZoneTree';
import css from './availabilityZoneTable.module.css';

export default function AvailabilityZoneTable({ zones = [], categories = [], contingents = [], organization, chartId, displayMode = 'contingents', search = '', handleOpenZone }) {
    const { t } = useTranslation();
    const { displayedZones } = useZonesTree(zones, search);

    if (!zones.length) {
        return <Card.EmptyState icon={<Icon name="list" />} title={t('ticket.seating.no_zones', 'No zones')} />;
    }

    if (!displayedZones.length) {
        return <Card.EmptyState icon={<Icon name="list" />} title={t('ticket.charts.zone.empty-search')} />;
    }

    return (
        <Table className={css['zone-table']} useNew>
            <Table.Header>
                <Table.Tr>
                    <th>{t('ticket.chart.zone')}</th>
                    <th>{t('ticket.chart.type')}</th>
                    <th>{t('ticket.chart.availability')}</th>
                    <th>{displayMode === 'contingents' ? t('ticket.chart.contingents') : t('ticket.chart.categories')}</th>
                    <th>{t('ticket.manager.tableHeader.actions')}</th>
                </Table.Tr>
            </Table.Header>
            <Table.TBody>
                {displayedZones.map(zone => (
                    <ZoneRow
                        key={zone.pk}
                        zone={zone}
                        depth={0}
                        handleOpenZone={handleOpenZone}
                        displayMode={displayMode}
                        categories={categories}
                        contingents={contingents}
                        chartId={chartId}
                        organization={organization}
                    />
                ))}
            </Table.TBody>
        </Table>
    );
}

function ZoneRow({ zone, depth = 0, displayMode, categories, contingents, chartId, organization, handleOpenZone }) {
    const { t } = useTranslation();
    const [showChildren, setShowChildren] = React.useState(true);

    const seated = React.useMemo(() => {
        if (zone.contingents.length === 0) return false;
        return true;
    }, [zone]);

    const availabilityText = zone.quota ? `${zone.quota.available || 0} / ${zone.quota.total ?? '-'}` : '-';

    const itemsIds = displayMode === 'contingents' ? zone.contingents || [] : zone.categories || [];
    const itemsList = displayMode === 'contingents' ? contingents : categories;

    const itemsWithMetadata = React.useMemo(() => {
        return itemsIds.map(id => {
            const meta = itemsList.find(x => x.uuid === id);
            return {
                id: id,
                name: meta?.name,
                color: meta?.color
            };
        });
    }, [itemsIds, itemsList]);

    return (
        <>
            <Table.Tr className={depth === 0 && zone.children?.length ? css['zone-group'] : undefined}>
                <Table.Td>
                    <div style={{ marginLeft: `${depth * 20}px` }} /* margin depends on depth - kept inline */>
                        {zone.children?.length && <Icon name={showChildren ? 'folder-open' : 'folder-closed'} onClick={() => setShowChildren(!showChildren)} />}
                        <span>{zone.name}</span>
                    </div>
                </Table.Td>
                {/* We want empty cells for parent zones */}
                {zone.children?.length ? (
                    <>
                        <Table.Td />
                        <Table.Td />
                        <Table.Td />
                        <Table.Td />
                    </>
                ) : (
                    <>
                        <Table.Td>
                            <Icon name={seated ? 'seat' : 'map-pin-inverted'} />
                            <span className={css['row-type-text']}>
                                {seated ? t('ticket.common.seating.numberedSeating', 'Seats') : t('ticket.common.seating.freeSeating', 'Open')}
                            </span>
                        </Table.Td>
                        <Table.Td>{availabilityText}</Table.Td>
                        <Table.Td>
                            <ColorDots items={itemsWithMetadata} />
                        </Table.Td>
                        <Table.Td>
                            {seated && (
                                <Button
                                    inverted
                                    small
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleOpenZone(zone);
                                    }}
                                    label={t('ticket.common.seating.see_seats')}
                                />
                            )}
                        </Table.Td>
                    </>
                )}
            </Table.Tr>
            {showChildren &&
                zone.children?.map(child => (
                    <ZoneRow
                        key={child.pk}
                        zone={child}
                        depth={depth + 1}
                        handleOpenZone={handleOpenZone}
                        displayMode={displayMode}
                        categories={categories}
                        contingents={contingents}
                        chartId={chartId}
                        organization={organization}
                    />
                ))}
        </>
    );
}
